import React from 'react'
import { useSelector } from 'react-redux'
import Grid from '@mui/material/Grid'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'

import TagSelector from '../tags/TagSelector'
import { typeTagMatchesPersonas } from '../../utils/tcairemFilters'

/**
 * T-CAIREM settings for a resource (issue #278): the "T-CAIREM's own" flag,
 * its conditionally-mandatory resource type and persona tags, and the paid
 * flag. The tag selectors only render while the flag is checked, and
 * unchecking clears both tag lists — the backend rejects type/persona tags
 * on non-T-CAIREM resources.
 *
 * Persona is selected first: each T-CAIREM Resource Type tag can optionally
 * declare which persona(s) it applies to, so the type selector only offers
 * types sharing at least one persona with the current selection (issue
 * #724; a type with no personas fits everything). Changing personas away
 * from an already-selected type that no longer fits prunes it from
 * tcairemTags — except when personas are cleared entirely, so a transient
 * clear while editing doesn't wipe the type selection.
 *
 * The Type selector also shows each option's personas as hint text
 * (issue #724), so the admin can see why a type is or isn't offered.
 */
const TCAIREMFieldsSection = ({
    isTcairem, setIsTcairem,
    isPaid, setIsPaid,
    tcairemTags, setTcairemTags,
    personaTags, setPersonaTags,
    forceShowRequiredErrors = false,
}) => {
    const typeTags = useSelector(state => state.tags?.tcairem) || []

    const handleTcairemToggle = (event) => {
        setIsTcairem(event.target.checked)
        if (!event.target.checked) {
            setTcairemTags([])
            setPersonaTags([])
        }
    }

    const handlePersonaChange = (names) => {
        setPersonaTags(names)
        if (names.length === 0) return
        const pruned = tcairemTags.filter(name => {
            const tag = typeTags.find(t => t.name === name)
            // An unknown/not-yet-loaded type tag is left alone here — it
            // isn't this handler's job to drop it.
            return !tag || typeTagMatchesPersonas(tag, names)
        })
        if (pruned.length !== tcairemTags.length) {
            setTcairemTags(pruned)
        }
    }

    return (
        <Grid item xs={12} md={6}>
            <FormControlLabel
                control={<Checkbox checked={isTcairem} onChange={handleTcairemToggle} />}
                label="T-CAIREM's own resource"
            />
            <FormControlLabel
                control={<Checkbox checked={isPaid} onChange={e => setIsPaid(e.target.checked)} />}
                label="Paid for institutions"
            />
            {isTcairem && (
                <>
                    <TagSelector
                        tagType='tcairemPersona'
                        label='T-CAIREM Persona'
                        selectedTags={personaTags}
                        onChange={handlePersonaChange}
                        required
                        forceShowErrors={forceShowRequiredErrors}
                    />
                    <TagSelector
                        tagType='tcairem'
                        label='T-CAIREM Resource Type'
                        selectedTags={tcairemTags}
                        onChange={setTcairemTags}
                        required
                        forceShowErrors={forceShowRequiredErrors}
                        filterOption={tag => typeTagMatchesPersonas(tag, personaTags) || tcairemTags.includes(tag.name)}
                        getOptionHint={tag => (tag.personas || []).join(', ') || 'All personas'}
                    />
                </>
            )}
        </Grid>
    )
}

export default TCAIREMFieldsSection
